import type { QueryClient } from '@tanstack/react-query';
import { redirect, type RouteObject } from 'react-router-dom';

import { getMyPermissionsQueryOptions } from '@/api/getMyPermissions';
import { getStudiosQueryOptions } from '@/api/getStudios';
import { gamesQueryKey, meQueryKey, studiosQueryKey } from '@/api/queryKeys';
import { outOfScope } from '@/app/requirePermissions';
import type { CrumbHandle } from '@/components/breadcrumb/breadcrumb';
import { STUDIO_PAGES } from '@/components/sidebar/pages';
import { StudioBreadcrumb } from '@/features/game/components/studioBreadcrumb';
import {
  setGameScope,
  setSandboxScope,
  setStudioScope,
} from '@/lib/api-client';
import { managesInStudio } from '@/models/permission';

import ErrorPage from '../error-page';
import Dashboard from '../game/dashboard';
import { gameRoute } from '../game/route';

const StudioDashboard = <Dashboard menu items={STUDIO_PAGES} />;

// The Studio the cache was last filled for: switching Studio must leave nothing of
// the previous one on screen or in cache (#469).
let cachedStudio: string | undefined;

const SESSION_SCOPES = new Set<unknown>([
  meQueryKey.all[0],
  studiosQueryKey.all[0],
  gamesQueryKey.all[0],
]);

export const studioRoute = (queryClient: QueryClient): RouteObject => ({
  path: 'studios/:studioCode',
  id: 'studio',
  handle: {
    crumb: ({ params }) => (
      <StudioBreadcrumb studioCode={params.studioCode ?? ''} />
    ),
    to: false,
  } satisfies CrumbHandle,
  loader: async ({ params }) => {
    const { studioCode } = params;
    if (!studioCode) throw new Error('Studio code is required');

    // Everything but the session-wide reads (the viewer, their Studios and Games),
    // which the breadcrumb keeps observing across the switch: removing those would
    // leave its observers on dead queries.
    if (cachedStudio !== undefined && cachedStudio !== studioCode)
      queryClient.removeQueries({
        predicate: ({ queryKey: [scope] }) => !SESSION_SCOPES.has(scope),
      });
    cachedStudio = studioCode;

    // Owns the studio part of the request scope, for this branch and every child.
    setStudioScope(studioCode);

    const { studios } = await queryClient.ensureQueryData(
      getStudiosQueryOptions(),
    );
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- see forbidden()
    if (!studios.some(({ code }) => code === studioCode)) throw outOfScope();

    return null;
  },
  children: [
    {
      element: StudioDashboard,
      // The studio dashboard is for whoever manages permissions anywhere in the
      // Studio: Grants are given there only. Anyone else reaches its Games from
      // the home list.
      loader: async ({ params }) => {
        // Back from a Game: the Studio is the whole scope again.
        setGameScope();
        setSandboxScope();

        const { roles } = await queryClient.ensureQueryData(
          getMyPermissionsQueryOptions(),
        );

        return managesInStudio(roles, params.studioCode ?? '')
          ? null
          : redirect('/studios');
      },
      children: [
        {
          errorElement: <ErrorPage />,
          children: [
            { index: true, loader: () => redirect('members') },
            // Static segments, so they outrank the sibling ':gameCode'.
            { path: 'games', lazy: () => import('./games') },
            { path: 'members', lazy: () => import('./members') },
          ],
        },
      ],
    },
    // The Game level owns its own folder; it nests here as the Studio's child.
    gameRoute(queryClient),
  ],
});
